import api from './api';

const resourceAvailabilityService = {
  // Get all resource availability with filtering and pagination
  getAll: async (params = {}) => {
    const response = await api.get('/resource-availability', { params });
    return response.data;
  },

  // Get availability by ID
  getById: async (id) => {
    const response = await api.get(`/resource-availability/${id}`);
    return response.data;
  },

  // Create new availability
  create: async (availabilityData) => {
    const response = await api.post('/resource-availability', availabilityData);
    return response.data;
  },

  // Update availability
  update: async (id, availabilityData) => {
    const response = await api.put(`/resource-availability/${id}`, availabilityData);
    return response.data;
  },

  // Delete availability
  delete: async (id) => {
    const response = await api.delete(`/resource-availability/${id}`);
    return response.data;
  },

  // Get availability by user
  getByUser: async (userId, params = {}) => {
    const response = await api.get(`/resource-availability/user/${userId}`, { params });
    return response.data;
  },

  // Get availability by date range
  getByDateRange: async (params = {}) => {
    const response = await api.get('/resource-availability/date-range', { params });
    return response.data;
  },

  // Get availability statistics
  getStatistics: async (params = {}) => {
    const response = await api.get('/resource-availability/statistics', { params });
    return response.data;
  },

  // Bulk create availability
  bulkCreate: async (bulkData) => {
    const response = await api.post('/resource-availability/bulk', bulkData);
    return response.data;
  },

  // Helper: Get availability status options
  getStatusOptions: () => [
    { value: 'Available', label: 'Available', icon: '✓', color: 'badge-success' },
    { value: 'Partially Available', label: 'Partially Available', icon: '◐', color: 'badge-warning' },
    { value: 'Unavailable', label: 'Unavailable', icon: '✗', color: 'badge-danger' },
    { value: 'On Leave', label: 'On Leave', icon: '🏖️', color: 'badge-info' }
  ],

  // Helper: Get status color
  getStatusColor: (status) => {
    const colors = {
      Available: 'badge-success',
      'Partially Available': 'badge-warning',
      Unavailable: 'badge-danger',
      'On Leave': 'badge-info'
    };
    return colors[status] || 'badge-secondary';
  },

  // Helper: Get status badge color
  getStatusBadgeColor: (status) => {
    const colors = {
      Available: 'badge-success',
      'Partially Available': 'badge-warning',
      Unavailable: 'badge-danger',
      'On Leave': 'badge-info'
    };
    return colors[status] || 'badge-secondary';
  },

  // Helper: Get status icon
  getStatusIcon: (status) => {
    const icons = {
      Available: '✓',
      'Partially Available': '◐',
      Unavailable: '✗',
      'On Leave': '🏖️'
    };
    return icons[status] || '?';
  },

  // Helper: Get calendar color for status
  getCalendarColor: (status) => {
    const colors = {
      Available: '#10b981',
      'Partially Available': '#f59e0b',
      Unavailable: '#ef4444',
      'On Leave': '#3b82f6'
    };
    return colors[status] || '#6b7280';
  },

  // Helper: Format date for display
  formatDate: (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  },

  // Helper: Format date for calendar
  formatCalendarDate: (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  },

  // Helper: Get day of week
  getDayOfWeek: (dateString) => {
    const date = new Date(dateString);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[date.getDay()];
  },

  // Helper: Check if date is weekend
  isWeekend: (dateString) => {
    const date = new Date(dateString);
    const day = date.getDay();
    return day === 0 || day === 6;
  },

  // Helper: Check if date is today
  isToday: (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  },

  // Helper: Check if date is in past
  isPast: (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  },

  // Helper: Validate availability data
  validateAvailabilityData: (availabilityData) => {
    const errors = {};

    if (!availabilityData.user_id) {
      errors.user_id = 'User is required';
    }
    if (!availabilityData.date) {
      errors.date = 'Date is required';
    }
    if (!availabilityData.available_hours) {
      errors.available_hours = 'Available hours is required';
    } else if (availabilityData.available_hours < 0 || availabilityData.available_hours > 24) {
      errors.available_hours = 'Available hours must be between 0 and 24';
    }
    if (!availabilityData.status) {
      errors.status = 'Status is required';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  // Helper: Generate date range
  generateDateRange: (startDate, endDate) => {
    const dates = [];
    const currentDate = new Date(startDate);
    const end = new Date(endDate);

    while (currentDate <= end) {
      dates.push(new Date(currentDate).toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  },

  // Helper: Group availability by date
  groupByDate: (availabilities) => {
    return availabilities.reduce((acc, availability) => {
      const date = availability.date;
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(availability);
      return acc;
    }, {});
  },

  // Helper: Group availability by user
  groupByUser: (availabilities) => {
    return availabilities.reduce((acc, availability) => {
      const userId = availability.user_id;
      if (!acc[userId]) {
        acc[userId] = {
          user: availability.user,
          availabilities: [],
          totalHours: 0,
          averageHours: 0
        };
      }
      acc[userId].availabilities.push(availability);
      acc[userId].totalHours += parseFloat(availability.available_hours);
      return acc;
    }, {});
  },

  // Helper: Calculate total available hours
  calculateTotalHours: (availabilities) => {
    return availabilities.reduce((sum, availability) => {
      return sum + parseFloat(availability.available_hours);
    }, 0).toFixed(2);
  },

  // Helper: Calculate average available hours
  calculateAverageHours: (availabilities) => {
    if (availabilities.length === 0) return 0;
    const total = resourceAvailabilityService.calculateTotalHours(availabilities);
    return (parseFloat(total) / availabilities.length).toFixed(2);
  },

  // Helper: Get availability summary
  getAvailabilitySummary: (availabilities) => {
    const summary = {
      total: availabilities.length,
      byStatus: {
        Available: 0,
        'Partially Available': 0,
        Unavailable: 0,
        'On Leave': 0
      },
      totalHours: 0,
      averageHours: 0
    };

    let totalHours = 0;

    availabilities.forEach(availability => {
      // By status
      if (summary.byStatus[availability.status] !== undefined) {
        summary.byStatus[availability.status]++;
      }

      // Total hours
      totalHours += parseFloat(availability.available_hours);
    });

    summary.totalHours = totalHours.toFixed(2);
    summary.averageHours = availabilities.length > 0 
      ? (totalHours / availabilities.length).toFixed(2) 
      : 0;

    return summary;
  },

  // Helper: Filter by status
  filterByStatus: (availabilities, status) => {
    return availabilities.filter(availability => availability.status === status);
  },

  // Helper: Filter by date range
  filterByDateRange: (availabilities, startDate, endDate) => {
    return availabilities.filter(availability => {
      const date = new Date(availability.date);
      const start = new Date(startDate);
      const end = new Date(endDate);
      return date >= start && date <= end;
    });
  },

  // Helper: Get available users for a date
  getAvailableUsers: (availabilities, date) => {
    return availabilities.filter(availability => 
      availability.date === date && availability.status === 'Available'
    );
  },

  // Helper: Get unavailable users for a date
  getUnavailableUsers: (availabilities, date) => {
    return availabilities.filter(availability => 
      availability.date === date && 
      (availability.status === 'Unavailable' || availability.status === 'On Leave')
    );
  },

  // Helper: Format calendar events
  formatCalendarEvents: (availabilities) => {
    return availabilities.map(availability => ({
      id: availability.id,
      title: `${availability.user?.first_name} ${availability.user?.last_name} - ${availability.status}`,
      date: availability.date,
      backgroundColor: resourceAvailabilityService.getCalendarColor(availability.status),
      borderColor: resourceAvailabilityService.getCalendarColor(availability.status),
      extendedProps: {
        user: availability.user,
        available_hours: availability.available_hours,
        status: availability.status,
        notes: availability.notes
      }
    }));
  },

  // Helper: Get calendar view data
  getCalendarViewData: (availabilities, startDate, endDate) => {
    const dates = resourceAvailabilityService.generateDateRange(startDate, endDate);
    const grouped = resourceAvailabilityService.groupByDate(availabilities);
    
    return dates.map(date => ({
      date,
      dayOfWeek: resourceAvailabilityService.getDayOfWeek(date),
      isWeekend: resourceAvailabilityService.isWeekend(date),
      isToday: resourceAvailabilityService.isToday(date),
      availabilities: grouped[date] || [],
      totalUsers: (grouped[date] || []).length,
      availableUsers: resourceAvailabilityService.getAvailableUsers(availabilities, date).length,
      unavailableUsers: resourceAvailabilityService.getUnavailableUsers(availabilities, date).length
    }));
  },

  // Helper: Sort by date
  sortByDate: (availabilities, ascending = true) => {
    return [...availabilities].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return ascending ? dateA - dateB : dateB - dateA;
    });
  },

  // Helper: Sort by available hours
  sortByHours: (availabilities, ascending = false) => {
    return [...availabilities].sort((a, b) => {
      const hoursA = parseFloat(a.available_hours);
      const hoursB = parseFloat(b.available_hours);
      return ascending ? hoursA - hoursB : hoursB - hoursA;
    });
  },

  // Helper: Get default available hours (standard work day)
  getDefaultAvailableHours: () => 8.0,

  // Helper: Get hours color based on availability
  getHoursColor: (hours) => {
    if (hours === 0) return 'text-red-600';
    if (hours < 4) return 'text-orange-600';
    if (hours < 8) return 'text-yellow-600';
    return 'text-green-600';
  },

  // Helper: Calculate capacity percentage
  calculateCapacityPercentage: (availableHours, standardHours = 8) => {
    return ((parseFloat(availableHours) / standardHours) * 100).toFixed(0);
  },

  // Helper: Get capacity color
  getCapacityColor: (percentage) => {
    if (percentage === 0) return 'bg-red-500';
    if (percentage < 50) return 'bg-orange-500';
    if (percentage < 100) return 'bg-yellow-500';
    return 'bg-green-500';
  }
};

export default resourceAvailabilityService;

